import { useState, useEffect, useContext } from "react";
import {
  Users,
  Send,
  MousePointer,
  Percent,
  Search,
  Plus,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Loader2,
} from "lucide-react";
import StatCard from "../../components/StatCard";
import CampaignModal from "../../components/CampaignModal";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import Layout from "../../components/Layout/Layout";
import SidebarList from "../../components/Sidebar";
import { AuthContext } from "../../hoc/AuthContext";

const baseUrl = process.env.REACT_APP_NOTIFICATION_BASE_URL;
const DOMAIN_ID = "68d2776bcc65b63c7e260fba";
const ANALYTICS_URL = `${baseUrl}analytics/topics/record/marathijagran.com`;

export default function CampaignsList() {
  const { selectedDomain, authUserData } = useContext(AuthContext);
  let user = authUserData?.userData?.first_name || "user";
  const domainUrl = selectedDomain.domain_url;
  const [campaigns, setCampaigns] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [analyticsData, setAnalyticsData] = useState({
    totalTopics: 0,
    totalNotification: 0,
  });
  const [showCreate, setShowCreate] = useState(false);
  const itemsPerPage = 10;
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Dynamic stats array using API data
  const stats = [
    {
      name: "Total Category",
      value: analyticsData.totalTopics.toLocaleString(),
      change: "", // Static for now, update if API provides this
      changeType: "positive",
      icon: Users,
      iconBg: "bg-red-50",
      iconColor: "text-red-600",
    },
    {
      name: "Notifications Sent",
      value: analyticsData.totalNotification.toLocaleString(),
      change: "", // Static for now, update if API provides this
      changeType: "positive",
      icon: Send,
      iconBg: "bg-green-50",
      iconColor: "text-green-600",
    },
  ];
  useEffect(() => {
    if (selectedDomain) {
      const find = selectedDomain?.accessList?.find(
        (el) => el.route_url.trim() == "services/notification/setting"
      );

      if (find) {
        setShowCreate(true);
      }
    }
  }, [selectedDomain]);
  // Fetch analytics data
  useEffect(() => {
    const fetchAnalytics = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await fetch(
          `${baseUrl}analytics/topics/record/${domainUrl}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch analytics data");
        }

        const result = await response.json();
        if (result.status !== 200) {
          throw new Error(result.error || "Analytics API error");
        }

        setAnalyticsData({
          totalTopics: result.data.totalTopics,
          totalNotification: result.data.totalNotification,
        });
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    if (domainUrl) fetchAnalytics();
  }, [domainUrl]);
  const fetchCampaigns = async (domain) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${baseUrl}topic/get-by-domain/${domain}?limit=${itemsPerPage}&page=${currentPage}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch topics");
      }

      const result = await response.json();
      if (result.status !== 200) {
        throw new Error(result.message || "API error");
      }

      const mappedCampaigns = result.data.topics.map((topic) => ({
        id: topic.id,
        name: topic.name,
        author: topic.createdBy,
        notificationCount: topic.notificationCount,
        sent: topic.notificationCount,
        clicked: 0,
        ctr: topic.ctr || 0 + "%",

        date: new Date(topic.createdAt).toISOString().split("T")[0],
        status: topic.status.toLowerCase(),
        description: topic.description,
        subscriberCount: topic.subscriberCount,
      }));

      setCampaigns(mappedCampaigns);
      setTotalCount(result.data.totalCount);
      setTotalPages(Math.ceil(result.data.totalCount / itemsPerPage));
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };
  // Fetch campaigns from API (unchanged)
  useEffect(() => {
    if (domainUrl) fetchCampaigns(domainUrl);
  }, [currentPage, itemsPerPage, domainUrl]);

  // Handle save campaign (unchanged)
  const handleSaveCampaign = async (campaignData) => {
    setIsLoading(true);
    setError(null);

    const payload = {
      name: campaignData.name,
      description: campaignData.description,
      domainId: domainUrl,
      status: campaignData.status.toUpperCase(),
      modifiedBy: "admin",
    };

    try {
      let response;
      if (editingCampaign) {
        response = await fetch(`${baseUrl}topic/${editingCampaign.id}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });
      } else {
        payload.parent = 0;
        payload.createdBy = user;
        response = await fetch(`${baseUrl}topic`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });
      }

      if (!response.ok) {
        throw new Error(
          `Failed to ${editingCampaign ? "update" : "create"} topic`
        );
      }

      const result = await response.json();
      if (result.status !== 200) {
        throw new Error(result.message || "API error");
      }

      fetchCampaigns(domainUrl);

      setEditingCampaign(null);
      setIsModalOpen(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (campaign) => {
    setEditingCampaign(campaign);
    setIsModalOpen(true);
  };

  const filteredCampaigns = campaigns.filter((campaign) => {
    const matchesSearch = campaign.name
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    const matchesDate = selectedDate
      ? campaign.date.startsWith(selectedDate)
      : true;
    return matchesSearch && matchesDate;
  });

  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, totalCount);
  return (
    <Layout bodynew={true}>
      <div className="flex min-h-screen w-full">
        <SidebarList />
        <main className="flex-1 overflow-auto">
          <div className="p-6 space-y-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground">
                Category bucket
              </h1>
              <p className="text-muted-foreground mt-1">
                Manage and track your notification Category
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
              {isLoading ? (
                <div className="col-span-full flex justify-center items-center py-10">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <span className="ml-2 text-muted-foreground">
                    Loading stats...
                  </span>
                </div>
              ) : (
                stats.map((stat) => <StatCard key={stat.name} {...stat} />)
              )}
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            <div className="bg-card rounded-xl border border-border overflow-hidden bg-white">
              <div className="flex flex-col lg:flex-row gap-4 p-6 border-b border-border">
                {/* <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search topics..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                    disabled={isLoading}
                  />
                </div> */}
                <div className="flex gap-3">
                  {/* <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="date"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      className="pl-10 w-full lg:w-auto"
                      disabled={isLoading}
                    />
                  </div> */}
                  {showCreate && (
                    <Button
                      onClick={() => {
                        setEditingCampaign(null);
                        setIsModalOpen(true);
                      }}
                      className="whitespace-nowrap"
                      disabled={isLoading}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Create Category
                    </Button>
                  )}
                </div>
              </div>

              <div className="overflow-x-auto">
                {isLoading ? (
                  <div className="flex justify-center items-center py-10">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    <span className="ml-2 text-muted-foreground">
                      Loading topics...
                    </span>
                  </div>
                ) : (
                  <table className="w-full">
                    <thead className="bg-secondary/50">
                      <tr>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">
                          Topics
                        </th>
                        <th className="px-6 py-4 text-right text-sm font-semibold text-foreground">
                          Notification Count
                        </th>
                        <th className="px-6 py-4 text-right text-sm font-semibold text-foreground">
                          Subscriber Count
                        </th>
                        <th className="px-6 py-4 text-right text-sm font-semibold text-foreground">
                          Status
                        </th>
                        <th className="px-6 py-4 text-right text-sm font-semibold text-foreground">
                          CTR
                        </th>
                        <th className="px-6 py-4 text-right text-sm font-semibold text-foreground">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {filteredCampaigns.length === 0 ? (
                        <tr>
                          <td
                            colSpan="4"
                            className="px-6 py-10 text-center text-muted-foreground"
                          >
                            No topics found
                          </td>
                        </tr>
                      ) : (
                        filteredCampaigns.map((campaign) => (
                          <tr
                            key={campaign.id}
                            className="hover:bg-secondary/30 transition-colors"
                          >
                            <td className="px-6 py-4">
                              <div>
                                <p className="font-medium text-foreground">
                                  {campaign.name}
                                </p>
                                <p className="text-sm text-muted-foreground">
                                  By {campaign.author} • {campaign.date}
                                </p>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-right text-sm text-foreground">
                              {campaign.notificationCount}
                            </td>
                            <td className="px-6 py-4 text-right text-sm text-foreground">
                              {campaign.subscriberCount}
                            </td>
                            <td className="px-6 py-4 text-right">
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  campaign.status === "active"
                                    ? "bg-green-100 text-green-800"
                                    : "bg-gray-100 text-gray-800"
                                }`}
                              >
                                {campaign.status}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-right text-sm text-foreground">
                              {campaign.ctr}
                            </td>
                            <td className="px-6 py-4 text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEdit(campaign)}
                                className="text-primary hover:text-primary"
                                // disabled={isLoading}
                                disabled={
                                  isLoading ||
                                  campaign?.name?.toLowerCase() == "all"
                                }
                              >
                                Edit
                              </Button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                )}
              </div>

              {!isLoading && totalCount > 0 && (
                <div className="flex items-center justify-between px-6 py-4 border-t border-border">
                  <p className="text-sm text-muted-foreground">
                    Showing {startIndex} to {endIndex} of {totalCount} topics
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <div className="flex gap-1">
                      {Array.from(
                        { length: Math.min(totalPages, 5) },
                        (_, i) => {
                          let page;
                          if (totalPages <= 5) {
                            page = i + 1;
                          } else if (currentPage <= 3) {
                            page = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            page = totalPages - 4 + i;
                          } else {
                            page = currentPage - 2 + i;
                          }
                          return page;
                        }
                      ).map((page) => (
                        <Button
                          key={page}
                          variant={currentPage === page ? "default" : "outline"}
                          size="sm"
                          onClick={() => setCurrentPage(page)}
                          className="w-10"
                        >
                          {page}
                        </Button>
                      ))}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setCurrentPage((p) => Math.min(totalPages, p + 1))
                      }
                      disabled={currentPage === totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <CampaignModal
              isOpen={isModalOpen}
              onClose={() => {
                setIsModalOpen(false);
                setEditingCampaign(null);
              }}
              campaign={editingCampaign}
              onSave={handleSaveCampaign}
            />
          </div>
        </main>
      </div>
    </Layout>
  );
}
